import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useStaticQuery, graphql } from 'gatsby'
import Img, { FluidObject } from 'gatsby-image'

import { Container } from 'components/Container'
import { backgroundColors, colors } from 'styles/colors'
import { displayWidth } from 'styles/width'
import { mobileAfterBorder } from 'styles/mobileAfterBorder'
import { Title, TitleH1 } from 'components/TitleComponent'
import { getDataByLanguage } from 'utils/getDataByLanguage'
import { SliderComponent } from 'components/SliderComponent'
import { indent } from 'styles/indent'
import FullScreen from 'assets/icons/fullScreen.svg'
import { ModalCarousel } from 'components/ModalCarousel'
import { ProjectData } from 'layout/Project'
import { sendEvent } from 'tracking'
import { headerBg } from 'styles/headerBg'
import { sendForm } from 'components/form/api'
import { Button } from 'components/Button'
import { LeftSidebar, RightSidebar } from 'components/SideBar'

const ExampleOfProjectWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    background-color: ${backgroundColors.index};
    position: relative;
    border-bottom: 1px solid ${colors.dark};
    ${mobileAfterBorder}
    :before {
        ${headerBg}
    }
`

const TitleStyledH1 = styled(TitleH1)`
    margin-top: 8px;
    @media (min-width: ${displayWidth.tablet}) {
        margin-top: 32px;
        margin-left: 0;
        margin-right: 0;
    }
`
const TitleStyled = styled(Title)`
    @media (min-width: ${displayWidth.tablet}) {
        margin-left: 0;
        margin-right: 0;
    }
`
const HeroColumn = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 ${indent.heroColumnTablet} 24px;
    border-bottom: 1px solid ${colors.dark};
    @media (min-width: ${displayWidth.tablet}) {
        border-bottom: none;
        border-right: 1px solid ${colors.dark};
    }
    @media (min-width: ${displayWidth.desktop}) {
        padding: 0 ${indent.heroColumnDesktop} 24px;
    }
`

const ImgStyled = styled(Img)<{ fluid: FluidObject }>`
    width: 100%;
    height: 100%;
`

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    background-color: ${colors.white};
    position: relative;
`
const WrapperMobile = styled(Wrapper)`
    @media (min-width: ${displayWidth.tablet}) {
        display: none;
    }
`
const WrapperDesktop = styled(Wrapper)`
    display: none;
    @media (min-width: ${displayWidth.tablet}) {
        display: flex;
    }
`
const FullScreenButton = styled(FullScreen)`
    fill: ${colors.white};
    stroke: ${colors.dark};
    stroke-width: 1px;
    position: absolute;
    top: 10px;
    right: 26px;
    width: 50px;
    height: 50px;
    cursor: pointer;
    z-index: 3;
    display: none;
    @media (min-width: ${displayWidth.tablet}) {
        display: block;
    }
    @media (min-width: ${displayWidth.desktop}) {
        right: 10px;
    }
`

const ButtonStyled = styled(Button)`
    align-self: center;
    width: 220px;
    a {
        text-decoration: none;
        color: #fff;
        font-size: 16px;
    }
    @media (min-width: ${displayWidth.desktop}) {
        margin: 30px 0;
        width: 272px;
    }
`

export const ExamplesOfProjectsHome = ({
    withH2Header = false,
    titleTetx,
    autoOpenModal = false,
}: {
    withH2Header?: boolean
    titleTetx?: string
    autoOpenModal?: boolean
}) => {
    const { i18n, t } = useTranslation()
    const [isModalOpen, setModalIsOpen] = useState(false)
    const data = useStaticQuery(graphql`
        query {
            ua: allFile(
                filter: { relativeDirectory: { eq: "projectExampleHome" } }
                sort: { fields: absolutePath }
            ) {
                edges {
                    node {
                        id
                        childImageSharp {
                            fluid(srcSetBreakpoints: [3000], quality: 100) {
                                ...GatsbyImageSharpFluid
                            }
                            parent {
                                ... on File {
                                    name
                                }
                            }
                        }
                    }
                }
            }
            ru: allFile(
                filter: { relativeDirectory: { eq: "projectExampleHomeRu" } }
                sort: { fields: absolutePath }
            ) {
                edges {
                    node {
                        id
                        childImageSharp {
                            fluid(srcSetBreakpoints: [3000], quality: 100) {
                                ...GatsbyImageSharpFluid
                            }
                            parent {
                                ... on File {
                                    name
                                }
                            }
                        }
                    }
                }
            }
            allExampleImageDataYaml {
                edges {
                    node {
                        items {
                            title
                            alt
                        }
                        parent {
                            ... on File {
                                name
                            }
                        }
                    }
                }
            }
            allExamplesOfProjectsHomeYaml {
                edges {
                    node {
                        title
                        parent {
                            ... on File {
                                name
                            }
                        }
                    }
                }
            }
        }
    `)
    const examplesOfProjectsHomeYaml = getDataByLanguage(
        data.allExamplesOfProjectsHomeYaml,
        i18n.language
    )
    const imagesData = getDataByLanguage(
        data.allExampleImageDataYaml,
        i18n.language
    )

    const { title } = examplesOfProjectsHomeYaml
    const sliderSettings = {
        infinite: true,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    arrows: false,
                    dots: true,
                },
            },
        ],
    }
    const filesDesktop = i18n.language === 'ru' ? data.ru.edges : data.ua.edges
    const examplePdfUrl =
        i18n.language === 'ru'
            ? 'https://clearline.com.ua/example_house_RU.pdf'
            : 'https://clearline.com.ua/example_house_UA.pdf'

    const clearOpenModalParam = () => {
        if (typeof window === 'undefined') {
            return
        }

        const currentUrl = new URL(window.location.href)
        if (!currentUrl.searchParams.has('openModal')) {
            return
        }

        currentUrl.searchParams.delete('openModal')
        const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
        window.history.replaceState(window.history.state, '', nextUrl)
    }

    useEffect(() => {
        if (autoOpenModal) {
            setModalIsOpen(true)
        }
    }, [autoOpenModal])

    return (
        <ExampleOfProjectWrapper>
            <LeftSidebar background={backgroundColors.index} />
            <Container columns={'1fr'} tabletColumns={'1fr 2fr'}>
                <HeroColumn>
                    {withH2Header ? (
                        <TitleStyled>{titleTetx ?? title}</TitleStyled>
                    ) : (
                        <TitleStyledH1>{titleTetx ?? title}</TitleStyledH1>
                    )}
                    <ButtonStyled
                        onClick={() => {
                            sendForm(`Скачали проект дома`, {})
                            sendEvent('Click', {
                                eventCategory: 'DownloadExempleHome',
                                placement: 'ExampleOfProjectHome',
                            })
                        }}
                        id="DownloadExample"
                    >
                        <a href={examplePdfUrl} download>
                            {t('downloadProject')}
                        </a>
                    </ButtonStyled>
                </HeroColumn>
                <WrapperDesktop>
                    <FullScreenButton
                        onClick={() => {
                            sendEvent('FullScreen', {
                                eventCategory: 'SliderExample',
                                placement: 'ExampleOfProject',
                            })
                            setModalIsOpen(true)
                        }}
                    />
                    <SliderComponent
                        {...sliderSettings}
                        afterChange={(current: number) => {
                            sendEvent('ShowSlide', {
                                eventCategory: 'SliderExample',
                                currentSlide: `${current}`,
                                placement: 'ExampleOfProject',
                            })
                        }}
                    >
                        {filesDesktop.map(
                            (
                                item: {
                                    node: {
                                        childImageSharp: {
                                            fluid: FluidObject
                                            parent: { name: string }
                                        }
                                    }
                                },
                                index: number
                            ) => {
                                return (
                                    <ImgStyled
                                        key={index}
                                        fluid={item.node.childImageSharp.fluid}
                                        imgStyle={{
                                            objectFit: 'containe',
                                        }}
                                        alt={imagesData.items[index]?.alt}
                                        title={imagesData.items[index]?.title}
                                    />
                                )
                            }
                        )}
                    </SliderComponent>
                </WrapperDesktop>
                <WrapperMobile>
                    <SliderComponent
                        {...sliderSettings}
                        afterChange={(current: number) => {
                            sendEvent('ShowSlide', {
                                eventCategory: 'SliderExample',
                                currentSlide: `${current + 1}`,
                                placement: 'ExampleOfProject',
                            })
                        }}
                    >
                        {filesDesktop.map(
                            (
                                item: {
                                    node: {
                                        childImageSharp: {
                                            fluid: FluidObject
                                            parent: { name: string }
                                        }
                                    }
                                },
                                index: number
                            ) => {
                                return (
                                    <ImgStyled
                                        key={index}
                                        fluid={item.node.childImageSharp.fluid}
                                        imgStyle={{
                                            objectFit: 'containe',
                                        }}
                                        alt={imagesData.items[index]?.alt}
                                        title={imagesData.items[index]?.title}
                                    />
                                )
                            }
                        )}
                    </SliderComponent>
                </WrapperMobile>
            </Container>
            <RightSidebar />
            <ModalCarousel
                data={filesDesktop.map(
                    ({ node }: { node: ProjectData }) => node
                )}
                isModalOpen={isModalOpen}
                closeHandler={() => {
                    clearOpenModalParam()
                    setModalIsOpen(false)
                }}
                initialSlideIndex={0}
            />
        </ExampleOfProjectWrapper>
    )
}
